import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/lib/axios';
import { queryKeys } from '@/lib/queryKeys';
import type {
  ApiResponse,
  DsrRequestPayload,
  ConsentRequestPayload,
  VerifyEmailPayload,
  CancelRequestPayload,
  User,
} from '@/types/api';

// Login Mutation
export const useLogin = () => {
  const { login } = useAuth();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const { data } = await createApiClient(() => ({})).post<ApiResponse<User>>(
        '/api/privacycenter/login',
        credentials
      );
      if (data.isError) {
        throw new Error(data.responseException?.exceptionMessage || 'Login failed');
      }
      return data.result;
    },
    onSuccess: (userData) => {
      login(userData);
    },
  });
};

// Save Data Subject Request
export const useSaveDataSubjectRequest = () => {
  const { getHeaders } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestData: DsrRequestPayload) => {
      const api = createApiClient(getHeaders);
      const { data } = await api.post<ApiResponse<any>>(
        '/api/privacycenter/dsrrequest',
        { dataSubject: requestData }
      );
      return data;
    },
    onSuccess: (data) => {
      if (!data.isError) {
        // Invalidate relevant queries
        queryClient.invalidateQueries({ queryKey: queryKeys.dsrByEmail(data.result?.email || '') });

        // Navigate to home with success message
        navigate('/', {
          state: { message: data.message || 'Request submitted successfully' },
          replace: true,
        });
      }
    },
  });
};

// Save Consent Request
export const useSaveConsentRequest = () => {
  const { getHeaders } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestData: ConsentRequestPayload) => {
      const api = createApiClient(getHeaders);
      const { data } = await api.post<ApiResponse<any>>(
        '/api/privacycenter/consentrequest',
        requestData
      );
      return data;
    },
    onSuccess: (data, variables) => {
      if (!data.isError) {
        // Invalidate consent management query
        queryClient.invalidateQueries({ queryKey: queryKeys.consentManagement(variables.subjectEmail) });

        // Navigate to home with success message
        navigate('/', {
          state: { message: data.message || 'Consent preferences updated successfully' },
          replace: true,
        });
      }
    },
  });
};

// Verify Email
export const useVerifyEmail = () => {
  const { getHeaders } = useAuth();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (payload: VerifyEmailPayload) => {
      const api = createApiClient(getHeaders);
      const { data } = await api.post<ApiResponse<any>>(
        '/api/privacycenter/verifyemail',
        payload
      );
      return data;
    },
    onSuccess: (data) => {
      if (!data.isError) {
        navigate('/', {
          state: { message: data.message || 'Email verified successfully' },
          replace: true,
        });
      }
    },
  });
};

// Cancel Data Subject Request
export const useCancelDataSubjectRequest = () => {
  const { getHeaders } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CancelRequestPayload) => {
      const api = createApiClient(getHeaders);
      const { data } = await api.post<ApiResponse<any>>(
        '/api/privacycenter/cancelrequest',
        payload
      );
      return data;
    },
    onSuccess: (data, variables) => {
      if (!data.isError) {
        // Invalidate DSR queries
        queryClient.invalidateQueries({ queryKey: queryKeys.dsrByEmail(variables.email) });
      }
    },
  });
};
